import { z } from 'zod';

import type {
  BoardStatusBody,
  PropertyDefinitionBody,
  TaskBody,
} from '@/shared/api/generated/model';
import { todayISO } from '@/shared/lib/dates';

/**
 * Фильтры, сортировка и поиск доски живут в адресе: ссылку на «мои срочные
 * на этой неделе» можно переслать, а «назад» возвращает прежний набор.
 *
 * Списки идентификаторов — строкой через запятую, а не JSON-массивом:
 * `?assignee=a,b` читается глазами и не ломается при копировании.
 * Некорректное значение любого параметра просто отбрасывается — адрес с
 * опечаткой открывает доску без этого фильтра, а не падает.
 */

export const VIEWS = ['board', 'list', 'table', 'calendar', 'timeline'] as const;
export const GROUPS = ['status', 'assignee', 'priority', 'none'] as const;
export const DUE_FILTERS = ['overdue', 'today', 'week', 'none'] as const;
export const SORTS = [
  'priority',
  'due',
  'created',
  'updated',
  'key',
  'title',
  'status',
  'start',
] as const;
export const DIRECTIONS = ['asc', 'desc'] as const;

/**
 * Ссылка на своё свойство организации: `prop:<uuid>`. В таком виде свойство
 * встаёт в один ряд со встроенными полями — в сортировке, группировке,
 * столбцах таблицы и полях карточки (то же соглашение на сервере).
 */
export type PropRef = `prop:${string}`;

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const PROP_REF = new RegExp(`^prop:${UUID}$`, 'i');

export function isPropRef(value: string | undefined): value is PropRef {
  return value !== undefined && PROP_REF.test(value);
}

export function propRef(id: string): PropRef {
  return `prop:${id}`;
}

export function propID(ref: PropRef): string {
  return ref.slice('prop:'.length);
}

export type View = (typeof VIEWS)[number];
export type BuiltinGroup = (typeof GROUPS)[number];
export type Group = BuiltinGroup | PropRef;
export type DueFilter = (typeof DUE_FILTERS)[number];
export type BuiltinSort = (typeof SORTS)[number];
export type Sort = BuiltinSort | PropRef;
export type Direction = (typeof DIRECTIONS)[number];

const list = z.string().min(1).optional().catch(undefined);

const groupSchema = z
  .string()
  .refine(
    (value): value is Group => (GROUPS as readonly string[]).includes(value) || isPropRef(value),
  )
  .optional()
  .catch(undefined);

const sortSchema = z
  .string()
  .refine(
    (value): value is Sort => (SORTS as readonly string[]).includes(value) || isPropRef(value),
  )
  .optional()
  .catch(undefined);

/**
 * Фильтры по своим свойствам одной строкой: `id:a|b;id2:c` — id
 * определения и значения через `|`, свойства через `;`. Значения — id
 * вариантов, id людей или `true` для флажка.
 */
export type PropFilters = Record<string, string[]>;

export function parsePropFilters(value: string | undefined): PropFilters {
  const out: PropFilters = {};

  for (const part of (value ?? '').split(';')) {
    const [id, values] = part.split(':');

    if (id === undefined || values === undefined || !new RegExp(`^${UUID}$`, 'i').test(id)) {
      continue;
    }

    const items = values.split('|').filter((item) => item !== '');

    if (items.length > 0) {
      out[id] = items;
    }
  }

  return out;
}

export function encodePropFilters(filters: PropFilters): string | undefined {
  const parts = Object.entries(filters)
    .filter(([, values]) => values.length > 0)
    .map(([id, values]) => `${id}:${values.join('|')}`);

  return parts.length === 0 ? undefined : parts.join(';');
}

export const boardSearchSchema = z.object({
  view: z.enum(VIEWS).optional().catch(undefined),
  saved: z
    .string()
    .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i)
    .optional()
    .catch(undefined),
  month: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
    .optional()
    .catch(undefined),
  group: groupSchema,
  task: z
    .string()
    .regex(/^TSK-\d+$/i)
    .transform((value) => value.toUpperCase())
    .optional()
    .catch(undefined),
  q: z.string().min(1).optional().catch(undefined),
  mine: z.literal(true).optional().catch(undefined),
  assignee: list,
  reviewer: list,
  priority: list,
  tag: list,
  /** Спринты через запятую: id, `current` (идущий сейчас), `none` (вне спринтов). */
  sprint: list,
  due: z.enum(DUE_FILTERS).optional().catch(undefined),
  props: z
    .string()
    .min(1)
    .transform((value) => encodePropFilters(parsePropFilters(value)))
    .optional()
    .catch(undefined),
  sort: sortSchema,
  dir: z.enum(DIRECTIONS).optional().catch(undefined),
});

export type BoardSearch = z.infer<typeof boardSearchSchema>;

/** Ключи адреса, которые относятся к фильтрам (без поиска и сортировки). */
export const FILTER_KEYS = [
  'mine',
  'assignee',
  'reviewer',
  'priority',
  'tag',
  'sprint',
  'due',
] as const;

/** Особые значения фильтра спринта рядом с id. */
export const SPRINT_CURRENT = 'current';
export const SPRINT_NONE = 'none';

export type FilterKey = (typeof FILTER_KEYS)[number];

export function splitList(value: string | undefined): string[] {
  return value === undefined ? [] : value.split(',').filter((item) => item !== '');
}

export function joinList(values: string[]): string | undefined {
  return values.length === 0 ? undefined : values.join(',');
}

/** Сколько фильтров включено — для счётчика на кнопке. */
export function activeFilterCount(search: BoardSearch): number {
  return (
    FILTER_KEYS.filter((key) => search[key] !== undefined).length +
    Object.keys(parsePropFilters(search.props)).length
  );
}

const PRIORITY_RANK: Record<TaskBody['priority'], number> = {
  none: 0,
  low: 1,
  normal: 2,
  high: 3,
  urgent: 4,
};

/**
 * Направление по умолчанию: срочное и новое — сверху; даты, ключ, название
 * и колонки — по порядку.
 */
export function defaultDirection(sort: Sort): Direction {
  return sort === 'priority' || sort === 'created' || sort === 'updated' ? 'desc' : 'asc';
}

interface Context {
  /** Кто смотрит — для «только мои». */
  me: string | undefined;
  statuses: BoardStatusBody[];
  /** Идущий спринт проекта — для `sprint=current`; нет такого — фильтр пуст. */
  currentSprint?: string | undefined;
}

/** Подходит ли значение свойства задачи под фильтр. */
function matchesProp(value: unknown, wanted: string[]): boolean {
  if (Array.isArray(value)) {
    return value.some((item) => typeof item === 'string' && wanted.includes(item));
  }

  if (value === true) {
    return wanted.includes('true');
  }

  return typeof value === 'string' && wanted.includes(value);
}

function addDays(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y ?? 1970, (m ?? 1) - 1, (d ?? 1) + days);

  return `${String(date.getFullYear())}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/**
 * Применяет фильтры и поиск к списку задач доски. Порядок внутри колонки
 * сохраняется — им занимается sortTasks.
 *
 * «Просрочено» не считает задачи в завершающих колонках: сделанная с
 * опозданием задача — не долг.
 */
export function applyFilters(tasks: TaskBody[], search: BoardSearch, ctx: Context): TaskBody[] {
  const assignees = new Set(splitList(search.assignee));
  const reviewers = new Set(splitList(search.reviewer));
  const priorities = new Set(splitList(search.priority));
  const tags = new Set(splitList(search.tag));
  const sprints = new Set(splitList(search.sprint));
  const propFilters = parsePropFilters(search.props);
  const done = new Set(ctx.statuses.filter((s) => s.is_done).map((s) => s.id));
  const query = (search.q ?? '').trim().toLowerCase();
  const today = todayISO();
  const weekEnd = addDays(today, 7);

  return tasks.filter((task) => {
    const people = (task.assignees ?? []).map((p) => p.id);

    if (search.mine === true) {
      if (ctx.me === undefined) {
        return false;
      }

      if (!people.includes(ctx.me) && task.reviewer?.id !== ctx.me) {
        return false;
      }
    }

    if (assignees.size > 0 && !people.some((id) => assignees.has(id))) {
      return false;
    }

    if (reviewers.size > 0 && (task.reviewer === null || !reviewers.has(task.reviewer.id))) {
      return false;
    }

    if (priorities.size > 0 && !priorities.has(task.priority)) {
      return false;
    }

    if (tags.size > 0 && !(task.tags ?? []).some((g) => tags.has(g.id))) {
      return false;
    }

    if (sprints.size > 0) {
      const id = task.sprint === null ? null : task.sprint.id;
      const matches =
        (id === null && sprints.has(SPRINT_NONE)) ||
        (id !== null && sprints.has(id)) ||
        (id !== null && id === ctx.currentSprint && sprints.has(SPRINT_CURRENT));

      if (!matches) {
        return false;
      }
    }

    for (const [id, wanted] of Object.entries(propFilters)) {
      if (!matchesProp(task.properties[id], wanted)) {
        return false;
      }
    }

    if (search.due !== undefined) {
      const due = task.due_date ?? null;

      switch (search.due) {
        case 'none':
          if (due !== null) {
            return false;
          }

          break;
        case 'overdue':
          if (due === null || due >= today || done.has(task.status_id)) {
            return false;
          }

          break;
        case 'today':
          if (due !== today) {
            return false;
          }

          break;
        case 'week':
          if (due === null || due < today || due > weekEnd) {
            return false;
          }

          break;
      }
    }

    if (query !== '') {
      const haystack = `${task.key} ${task.title} ${task.description}`.toLowerCase();

      if (!haystack.includes(query)) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Сортировка внутри колонок. Без `sort` — ручной порядок с доски (позиция).
 * Задачи без дедлайна при сортировке по дедлайну всегда в конце: «нет
 * срока» — не «самый ранний срок».
 */
export function sortTasks(
  tasks: TaskBody[],
  sort: Sort | undefined,
  dir: Direction | undefined,
  statuses: BoardStatusBody[] = [],
  definitions: PropertyDefinitionBody[] = [],
) {
  if (sort === undefined) {
    return tasks;
  }

  const sign = (dir ?? defaultDirection(sort)) === 'asc' ? 1 : -1;
  const statusRank = new Map(statuses.map((st, index) => [st.id, index]));
  const byDate = (a: string | null, b: string | null): number => {
    if (a === null || b === null) {
      return a === b ? 0 : a === null ? 1 : -1;
    }

    return a.localeCompare(b) * sign;
  };

  // Своё свойство: пустые — всегда в конце, как задачи без дедлайна.
  // Выбор сравнивается по названию варианта, человек — по id (имени у
  // задачи нет), остальное — по самому значению.
  const propCompare = (ref: PropRef) => {
    const id = propID(ref);
    const definition = definitions.find((item) => item.id === id);
    const optionName = new Map(
      (definition?.options ?? []).map((option) => [option.id ?? option.name, option.name]),
    );
    const key = (task: TaskBody): number | string | null => {
      const value = task.properties[id];

      if (value === undefined || value === null || value === '' || value === false) {
        return null;
      }

      if (typeof value === 'number') {
        return value;
      }

      if (value === true) {
        return 1;
      }

      if (Array.isArray(value)) {
        return value
          .filter((item): item is string => typeof item === 'string')
          .map((item) => optionName.get(item) ?? item)
          .join(', ');
      }

      if (typeof value !== 'string') {
        return null;
      }

      return optionName.get(value) ?? value;
    };

    return (a: TaskBody, b: TaskBody): number => {
      const x = key(a);
      const y = key(b);

      if (x === null || y === null) {
        return x === y ? 0 : x === null ? 1 : -1;
      }

      if (typeof x === 'number' && typeof y === 'number') {
        return (x - y) * sign;
      }

      return String(x).localeCompare(String(y), 'ru', { numeric: true }) * sign;
    };
  };

  const compare = (a: TaskBody, b: TaskBody): number => {
    if (isPropRef(sort)) {
      return propCompare(sort)(a, b);
    }

    switch (sort) {
      case 'priority':
        return (PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority]) * sign;
      case 'due':
        return byDate(a.due_date ?? null, b.due_date ?? null);
      case 'start':
        return byDate(a.start_date ?? null, b.start_date ?? null);
      case 'key':
        return (a.number - b.number) * sign;
      case 'title':
        return a.title.localeCompare(b.title, 'ru') * sign;
      case 'status':
        return ((statusRank.get(a.status_id) ?? 0) - (statusRank.get(b.status_id) ?? 0)) * sign;
      case 'created':
        return a.created_at.localeCompare(b.created_at) * sign;
      case 'updated':
        return a.updated_at.localeCompare(b.updated_at) * sign;
    }
  };

  // Стабильно: при равенстве остаётся ручной порядок.
  return tasks
    .map((task, index) => ({ task, index }))
    .sort((a, b) => compare(a.task, b.task) || a.index - b.index)
    .map((item) => item.task);
}
